import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';

import { Field } from '~/components/Card/Field';
import { ModalScrollView } from '~/components/Modals/Modal.utils';
import { AppText } from '~/components/Text';
import { SubDistributionPill } from '~/screens/Support/Purchases/Purchase.ListItem';
import { PurchaseMix } from '~/screens/Support/Purchases/purchase.utils';
import { translate } from '~/utils/translation.utils';

const ModalKey = 'usePurchaseTariffDetails';

export const usePurchaseTariffDetails = () => {
  const { openModal, closeModal } = useModal();
  return {
    open: (purchase: PurchaseMix) => {
      openModal(<PurchaseTariffDetailsModal purchase={purchase} />, {
        key: ModalKey,
        type: 'action-sheet-full',
      });
    },
    close: () => {
      closeModal(ModalKey);
    },
  };
};

const PurchaseTariffDetailsModal = ({ purchase }: { purchase: PurchaseMix }) => {
  const { closeModal } = useModal();

  return (
    <Modal.Container w={800}>
      <Modal.Header
        onPress={() => {
          closeModal(ModalKey);
        }}
        title={translate('purchases.tariff.tariffInfo')}
        children={<SubDistributionPill purchase={purchase} />}
      />
      <Divider hairline />
      <ModalScrollView>
        <VStack style={{ maxHeight: 500 }}>
          <PurchaseTariffDetailsCard purchase={purchase} />
        </VStack>
      </ModalScrollView>
    </Modal.Container>
  );
};

const PurchaseTariffDetailsCard = ({ purchase }: { purchase: PurchaseMix }) => {
  //@ts-ignore
  const tariff = purchase.tariff as TariffDetail | undefined;
  if (!tariff) {
    return null;
  }

  return (
    <VStack space="2.5" p="2.5">
      <TariffInfo tariff={tariff} />
      <TariffList descriptionList={tariff.descriptionList || []} />
    </VStack>
  );
};

const TariffInfo = ({ tariff }: { tariff: TariffDetail }) => {
  return (
    <VStack space="2.5">
      <Field
        label={translate('purchases.tariff.name')}
        value={tariff.companyTariffName}
        color="primary"
        valueType="body1SemiBold"
      />
      <HStack space="2.5">
        <Field
          label={translate('purchases.tariff.amendable')}
          color={tariff.nonAmendable ? 'error' : 'success'}
          value={tariff.nonAmendable ? 'No' : 'Yes'}
        />
        <Field
          label={translate('purchases.tariff.refundable')}
          color={tariff.nonRefundable ? 'error' : 'success'}
          value={tariff.nonRefundable ? 'No' : 'Yes'}
        />
      </HStack>
      <HStack space="2.5">
        <Field
          label={translate('purchases.tariff.companyCode')}
          value={tariff.companyCode}
        />
        <Field
          label={translate('purchases.tariff.providerCode')}
          value={tariff.providerCode}
        />
      </HStack>
      <Field
        label={translate('purchases.tariff.defaultDescription')}
        value={tariff.description}
        valueType="small"
      />
    </VStack>
  );
};

const TariffList = ({ descriptionList }: { descriptionList: string[] }) => {
  return (
    <VStack space="2.5">
      <Divider />
      {descriptionList.map((description) => {
        return <TariffItem description={description} />;
      })}
    </VStack>
  );
};

const TariffItem = ({ description }: { description: string }) => {
  return (
    <VStack p="2.5" b={1} br={5}>
      <AppText.Small>{description}</AppText.Small>
    </VStack>
  );
};
interface TariffDetail {
  providerCode: string;
  companyCode: string;
  companyTariffName: string;
  description: string;
  descriptionList: string[];
  nonAmendable: boolean;
  nonRefundable: boolean;
}
